/**
 * A drink's aroma, rolled up from its build (#127, step 1).
 *
 * The flavour wheel (wheel.ts) reads one ingredient's profile at a time.
 * This is the step up from there: a spec's lines, each pointing at an
 * ingredient with its own flavour, combined into one profile for the drink
 * in the ten flavour-database families (flavour.ts). It is what the recipe screen
 * shows as "what this drink smells of", and it is the keystone for the guest
 * work that follows — a guest says "Spiced and Woody" in these same ten
 * families, and `rankSpecsForTaste` finds the drinks that are.
 *
 * Pure and deterministic: specs and ingredients in, numbers out.
 *
 * ## How a line is weighted
 *
 * By the volume it puts in the glass, because that is the one quantity every
 * line has and the one the rest of BarOS already trusts (calc.ts costs and
 * ABVs by it). `SpecLine.ml` is already resolved whatever the recipe wrote,
 * so a dash weighs its 0.8 mL and a "top" its assumed 60 mL.
 *
 * That makes bitters and drops nearly silent, which undersells how loud they
 * are on the nose. Accepted for now: a weighting by "aromatic intensity"
 * would need a number per ingredient that nobody has measured, and a guessed
 * multiplier would quietly decide what every drink smells of.
 *
 * The exception is a counted line — `each`: an egg white, a cherry, a twist.
 * Its stored millilitres are a different guess depending on where the spec
 * came from (the Airtable import says 30 mL per egg white; the workbook says
 * whatever its own column says), so it is weighted at `EACH_AROMA_ML` per
 * piece instead: present, and small.
 *
 * ## What a line contributes
 *
 * The ingredient's flavour-database family scores when the caller has resolved
 * them (`resolveFlavour` in flavour-link.ts — a linked ingredient, or a batch
 * blended from its build), spread across the ten families in proportion.
 * Failing that, its primary `aromaFamily`, all of the line's weight to that
 * one family. Failing that — water, ice, soda, or a dilution line — nothing,
 * and that is correct rather than a gap: those lines do not smell of
 * anything. A line whose ingredient the org does not hold at all is skipped
 * and reported, because there the drink's profile really is incomplete and
 * the screen should say so.
 *
 * Family scores come from the caller rather than the ingredient because they
 * live in the shared dataset, not on the org's rows (architecture D16). A
 * caller without them gets the coarser one-family answer, never an error.
 *
 * Only the ten families are counted. An `aromaFamily` saved in the old
 * twelve-family words is read as its new family (`toFlavourFamily`), and
 * anything else is ignored, so every result speaks the closed vocabulary a
 * guest's preferences are recorded in.
 */
import { FLAVOUR_FAMILY_KEYS, toFlavourFamily } from "./flavour.js";
/** The ten families, in wheel order. Used to break ties. */
export const AROMA_FAMILY_ORDER = FLAVOUR_FAMILY_KEYS;
/**
 * What one counted (`each`) item contributes, in millilitres of weight. A
 * little more than two dashes: enough to register, not enough to lead.
 */
export const EACH_AROMA_ML = 2;
/** The most families a guest may name as preferences. */
export const MAX_PREFERRED_FAMILIES = 3;
/**
 * The drink-style axis, closed — see `Spec.style`. Kept here, not in
 * library.ts, because the taste ranking is the first thing that needs it as
 * a list rather than as free text on a spec.
 */
export const SPEC_STYLES = ["Short", "Sour", "Bitter", "Refreshing"];
/** The aroma weight of one line, in millilitres. See the module note. */
export function lineAromaWeight(line) {
    const raw = line.unit === "each" ? (line.qty ?? 1) * EACH_AROMA_ML : line.ml;
    return Number.isFinite(raw) && raw > 0 ? raw : 0;
}
/**
 * One ingredient's aroma as fractions of one across the ten families, or null
 * when it has none. Resolved family scores win over the primary family.
 */
export function ingredientAroma(ing, flavour) {
    if (ing.kind === "dilution")
        return null;
    const fromScores = familyShares(flavour);
    if (fromScores)
        return fromScores;
    const family = toFlavourFamily(ing.aromaFamily);
    return family ? { [family]: 1 } : null;
}
/** Family scores as fractions of one, or null when there are none above zero. */
function familyShares(flavour) {
    if (!flavour)
        return null;
    const usable = FLAVOUR_FAMILY_KEYS.map((family, i) => [family, flavour.families[i] ?? 0]).filter(([, score]) => Number.isFinite(score) && score > 0);
    const total = usable.reduce((sum, [, score]) => sum + score, 0);
    if (total <= 0)
        return null;
    return Object.fromEntries(usable.map(([family, score]) => [family, score / total]));
}
/**
 * The drink-level aroma profile of a spec: its lines, volume-weighted, each
 * spread across its ingredient's families. See the module note for the rules.
 */
export function specAroma(spec, ingredientsById, flavours) {
    const sums = {};
    const unknown = [];
    const familyOnly = new Set();
    let linesCounted = 0;
    for (const line of spec.lines) {
        const ing = ingredientsById.get(line.ingredientId);
        if (!ing) {
            unknown.push(line.ingredientId);
            continue;
        }
        const weight = lineAromaWeight(line);
        const flavour = flavours?.get(line.ingredientId);
        const aroma = ingredientAroma(ing, flavour);
        if (!aroma || weight === 0)
            continue;
        linesCounted += 1;
        if (!familyShares(flavour))
            familyOnly.add(ing.ingredientId);
        for (const [family, share] of Object.entries(aroma)) {
            sums[family] = (sums[family] ?? 0) + share * weight;
        }
    }
    const total = Object.values(sums).reduce((a, b) => a + b, 0);
    const shares = {};
    if (total > 0) {
        for (const family of AROMA_FAMILY_ORDER) {
            const sum = sums[family];
            if (sum)
                shares[family] = sum / total;
        }
    }
    return {
        shares,
        unknownIngredientIds: unknown,
        familyOnlyIngredientIds: [...familyOnly],
        linesCounted,
    };
}
/**
 * The `n` families a drink leans on most, largest first. Ties keep the wheel
 * order of the ten so the same drink always lists the same way. Families
 * under `minShare` are left out — a 2% trace of Earthy from one dash is true
 * and not worth a chip.
 */
export function topAromaFamilies(shares, n = 3, minShare = 0) {
    return AROMA_FAMILY_ORDER
        .map((family) => ({ family, share: shares[family] ?? 0 }))
        .filter((f) => f.share > 0 && f.share >= minShare)
        .sort((a, b) => b.share - a.share)
        .slice(0, Math.max(0, n));
}
/**
 * Tidy preferences into the closed vocabulary: families and styles matched
 * case-insensitively to their reference spelling, unknown words dropped,
 * duplicates removed with the first kept, families capped at three.
 *
 * A family in the old twelve-family words is read as its new family, so a
 * guest who picked "Citrus" before the switch now likes Sharp; two old
 * picks that land on one family ("Fruity", "Sweet") become one.
 *
 * A guest form is the source, so this is lenient rather than throwing: a
 * stray word should not lose the rest of what they said.
 */
export function normaliseTastePreferences(prefs) {
    const pick = (values, read, cap) => {
        const out = [];
        for (const raw of values ?? []) {
            if (typeof raw !== "string")
                continue;
            const hit = read(raw);
            if (hit && !out.includes(hit))
                out.push(hit);
            if (out.length >= cap)
                break;
        }
        return out;
    };
    const style = (word) => SPEC_STYLES.find((v) => v.toLowerCase() === word.trim().toLowerCase()) ?? null;
    return {
        families: pick(prefs.families, toFlavourFamily, MAX_PREFERRED_FAMILIES),
        styles: pick(prefs.styles, style, SPEC_STYLES.length),
    };
}
/**
 * How much the style match counts when the guest named any styles. A quarter:
 * a drink that smells right in the wrong style still beats one that is the
 * right style and smells of nothing they asked for.
 */
export const STYLE_WEIGHT = 0.25;
/**
 * Score one spec against a guest's preferences.
 *
 * The family score is simply how much of the drink's aroma falls in the
 * families they named — a drink that is 60% Spice and Woody scores 0.6 for
 * "Spice, Woody". With no styles named that is the score. With styles named
 * it is blended: `(1 - STYLE_WEIGHT) × family + STYLE_WEIGHT × style`, style
 * being 1 when any of the spec's styles is one they asked for.
 *
 * Expects preferences already normalised (`normaliseTastePreferences`).
 * `flavours` is as for `specAroma`: finer when given, never required.
 */
export function tasteMatch(spec, ingredientsById, prefs, flavours) {
    const aroma = specAroma(spec, ingredientsById, flavours);
    // Guest records keep what was saved, so a guest from before the switch may
    // still say "Citrus"; it is read as Sharp here rather than missing everything.
    const wanted = new Set(prefs.families.map(toFlavourFamily).filter((f) => f !== null));
    const matched = topAromaFamilies(aroma.shares, AROMA_FAMILY_ORDER.length)
        .filter((f) => wanted.has(f.family));
    const familyScore = Math.min(1, matched.reduce((total, f) => total + f.share, 0));
    const wantedStyles = (prefs.styles ?? []).map((s) => s.toLowerCase());
    const styleMatch = wantedStyles.length > 0 && spec.style.some((s) => wantedStyles.includes(s.trim().toLowerCase()));
    const score = wantedStyles.length > 0
        ? (1 - STYLE_WEIGHT) * familyScore + STYLE_WEIGHT * (styleMatch ? 1 : 0)
        : familyScore;
    return {
        spec,
        score,
        familyScore,
        styleMatch,
        matchedFamilies: matched.map((f) => f.family),
        partial: aroma.unknownIngredientIds.length > 0,
    };
}
/**
 * Specs ranked for a guest, best first. Ties go to the stronger family score
 * and then to the name, so the order is stable. Drinks scoring zero are left
 * out: "you might like" should not pad itself with things that match nothing.
 *
 * Does no filtering by status or menu — the caller decides whether retired
 * drinks or ones off tonight's list are candidates.
 */
export function rankSpecsForTaste(specs, ingredientsById, prefs, flavours) {
    return specs
        .map((spec) => tasteMatch(spec, ingredientsById, prefs, flavours))
        .filter((m) => m.score > 0)
        .sort((a, b) => b.score - a.score ||
        b.familyScore - a.familyScore ||
        a.spec.name.localeCompare(b.spec.name));
}
//# sourceMappingURL=aroma.js.map